/**
 * @biomax/grounding — the anti-hallucination spine of the BioMax grounded platform.
 *
 * A `GroundedValue` is a number that cannot appear in an output without provenance.
 * It is exactly one of:
 *   - COMPUTED by an engine (with the derivation + determinism receipt shown),
 *   - CITED from a source whose claim was claim-checked (author-authoritative,
 *     mirroring the catalog's crossCheckClaim), or
 *   - REFUSED (ungroundable — no source, or the citation does not match the claim).
 *
 * `assertAllGrounded` deep-validates a response so a refused/ungroundable value can
 * never be silently presented. That is the mechanical guarantee that makes
 * hallucination structurally impossible in the output path: the reason layer builds
 * answers ONLY from GroundedValues, and this guard rejects any that aren't grounded.
 *
 * Pure + browser-safe: NO node:* imports. The network round-trip that resolves a PMID
 * lives in @biomax/grounded-tools (Node side); this package takes already-resolved
 * metadata and decides match / uncheckable / refuse.
 */

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

export interface DeterminismReceipt {
  readonly engine: string;
  readonly seed?: number;
  readonly ms?: number;
  readonly steps?: number;
}

/** Claim-vs-paper verdict. NOTE: `mismatch` never reaches a GroundedValue — a
 *  mismatched citation is REFUSED instead. A cited GroundedValue is only ever
 *  `match` | `uncheckable`. */
export type ClaimVerdict = 'match' | 'mismatch' | 'uncheckable';
export type CitedClaim = 'match' | 'uncheckable';

export type Provenance =
  | {
      readonly kind: 'computed';
      readonly engine: string;
      readonly method: string;
      /** Human-readable derivation, e.g. "Vd = CL·t½/ln2 = 1.46·2.93/0.693 = 6.17 L/kg". */
      readonly derivation: string;
      readonly inputs?: readonly GroundedValue[];
      readonly receipt?: DeterminismReceipt;
    }
  | {
      readonly kind: 'cited';
      readonly pmid: string;
      readonly doi?: string;
      readonly claimMatch: CitedClaim;
      readonly title?: string;
      readonly authors?: readonly string[];
      readonly year?: number;
      readonly quote?: string;
      readonly note?: string;
    }
  | {
      readonly kind: 'refused';
      readonly reason: string;
    };

export interface GroundedValue<T = number> {
  /** The value, or `null` iff `provenance.kind === 'refused'`. */
  readonly value: T | null;
  readonly unit?: string;
  readonly provenance: Provenance;
}

export class GroundingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GroundingError';
  }
}

// ---------------------------------------------------------------------------
// Constructors
// ---------------------------------------------------------------------------

export function computed<T>(
  value: T,
  meta: {
    engine: string;
    method: string;
    derivation: string;
    inputs?: readonly GroundedValue[];
    receipt?: DeterminismReceipt;
  },
  unit?: string,
): GroundedValue<T> {
  return { value, unit, provenance: { kind: 'computed', ...meta } };
}

export function cited<T>(
  value: T,
  c: {
    pmid: string;
    doi?: string;
    claimMatch: CitedClaim;
    title?: string;
    authors?: readonly string[];
    year?: number;
    quote?: string;
    note?: string;
  },
  unit?: string,
): GroundedValue<T> {
  return { value, unit, provenance: { kind: 'cited', ...c } };
}

export function refuse<T = number>(reason: string): GroundedValue<T> {
  return { value: null, provenance: { kind: 'refused', reason } };
}

// ---------------------------------------------------------------------------
// Guards — the mechanical guarantee
// ---------------------------------------------------------------------------

export function isGrounded<T>(v: GroundedValue<T>): boolean {
  return v.provenance.kind !== 'refused' && v.value !== null;
}

export function assertGrounded<T>(v: GroundedValue<T>, ctx?: string): void {
  if (!isGrounded(v)) {
    const reason = v.provenance.kind === 'refused' ? v.provenance.reason : 'value is null';
    throw new GroundingError(`Ungrounded value${ctx ? ` at ${ctx}` : ''}: ${reason}`);
  }
}

/** Heuristic: does this object look like a GroundedValue? (carries a provenance
 *  with a known kind). Used by the deep walk to stop recursing into a grounded
 *  value's own `.value`. */
function looksGrounded(x: unknown): x is GroundedValue {
  if (typeof x !== 'object' || x === null || !('provenance' in x)) return false;
  const prov = (x as { provenance: unknown }).provenance;
  return (
    typeof prov === 'object' &&
    prov !== null &&
    'kind' in prov &&
    (prov.kind === 'computed' || prov.kind === 'cited' || prov.kind === 'refused')
  );
}

/**
 * Deep-walk any response and validate that every GroundedValue it contains is
 * grounded (none `refused` / `null`). Throws `GroundingError` on the first
 * violation. Recurses into computed `inputs` too.
 *
 * This validates the GroundedValues present; it does not forbid plain structural
 * numbers (ids, counts, indices, timestamps). The reason layer additionally
 * constructs prose ONLY from GroundedValues, so a bare scientific number cannot
 * appear in a presented answer.
 */
export function assertAllGrounded(obj: unknown, path = '$'): void {
  if (looksGrounded(obj)) {
    assertGrounded(obj, path);
    const prov = obj.provenance;
    if (prov.kind === 'computed' && prov.inputs) {
      prov.inputs.forEach((inp, i) => assertAllGrounded(inp, `${path}.inputs[${i}]`));
    }
    return;
  }
  if (Array.isArray(obj)) {
    obj.forEach((v, i) => assertAllGrounded(v, `${path}[${i}]`));
    return;
  }
  // Map / Set entries are NOT visited by Object.entries — walk them explicitly so a
  // refused GroundedValue stashed in a Map value / Set element cannot slip past the
  // guard. (No current grounded result uses these, but the guard's promise is to
  // deep-walk ANY response; this closes that structural gap cheaply.)
  if (obj instanceof Map) {
    for (const [k, v] of obj.entries()) assertAllGrounded(v, `${path}.get(${String(k)})`);
    return;
  }
  if (obj instanceof Set) {
    let i = 0;
    for (const v of obj) assertAllGrounded(v, `${path}.set[${i++}]`);
    return;
  }
  if (typeof obj === 'object' && obj !== null) {
    for (const [k, v] of Object.entries(obj)) assertAllGrounded(v, `${path}.${k}`);
  }
}

// ---------------------------------------------------------------------------
// Claim check — author-authoritative. The ENGINE lives in ./claim-check (the
// ONE canonical citation claim-check, shared with the drug + pathogen catalog
// verifiers via a policy). Grounding binds the `GROUNDING_CLAIM_POLICY` and
// keeps its own `claimMatch` verdict-flow (surname→author path; else year
// path; else uncheckable) — re-expressed on the shared parse/token primitives
// so there is no longer a private copy of the parse + normalise + token logic.
// ---------------------------------------------------------------------------

import {
  parseClaimedSurnamesWith,
  parseClaimedYearsWith,
  authorTokens,
  normName,
  normToken,
  type ClaimCheckPolicy,
} from './claim-check.js';
export {
  crossCheckClaimWith,
  parseClaimedSurnamesWith,
  parseClaimedYearsWith,
  authorTokens,
  authorFamilyCandidates,
  normName,
  normToken,
  type ClaimCheckResult,
  type ClaimCheckVerdict,
  type ClaimCheckPolicy,
  type SurnameParseStrategy,
  type AuthorMatchMode,
  type ResolvedClaimMeta,
} from './claim-check.js';

/**
 * Grounding's claim-check policy: a GREEDY surname parse (every capitalised
 * word, original case) + WHOLE-TOKEN author matching ("cohen" ≠ "cohenstein")
 * + no title fallback + bare-year comparison. This reproduces the original
 * grounding-spine behaviour exactly; the catalog verifiers bind their own,
 * stricter, anchored policies against the same engine.
 */
export const GROUNDING_CLAIM_POLICY: ClaimCheckPolicy = {
  parseStrategy: 'greedy',
  noise: new Set(
    [
      'The', 'And', 'For', 'From', 'With', 'This', 'That', 'Per', 'Via', 'See',
      'Table', 'Figure', 'Fig', 'Appendix', 'Supplementary', 'Vol', 'No', 'pp',
      'WHO', 'CDC', 'FDA', 'EMA', 'NEJM', 'PMID', 'DOI',
    ].map(normName),
  ),
  authorMatchMode: 'whole-token',
  titleMatch: false,
  dedupYears: false,
  yearOnlyVerdict: 'compare',
};

/** Parse claimed author surnames from a citation note (grounding policy). */
export function parseClaimedSurnames(note: string): string[] {
  return parseClaimedSurnamesWith(note, GROUNDING_CLAIM_POLICY);
}

/** Parse 4-digit publication years (1900–2099) from a citation note. */
export function parseClaimedYears(note: string): number[] {
  return parseClaimedYearsWith(note, GROUNDING_CLAIM_POLICY);
}

/**
 * Decide whether a citation note's CLAIM (author/year named in the prose) matches
 * the metadata the PMID actually resolved to. Author is authoritative: if the note
 * names surnames and the resolved authors share none, that's a `mismatch` (this is
 * exactly how the homeopathy/myeloproliferative wrong-PMIDs were caught). Falls back
 * to year only when no surnames are checkable (or the resolved record has no author
 * list). `uncheckable` when the note carries no parseable author/year, or no metadata
 * was supplied (offline).
 */
export function claimMatch(
  note: string | undefined,
  meta: { authors?: readonly string[]; year?: number; title?: string } | null | undefined,
): ClaimVerdict {
  if (!note) return 'uncheckable';
  const surnames = parseClaimedSurnames(note);
  const years = parseClaimedYears(note);
  if (surnames.length === 0 && years.length === 0) return 'uncheckable';
  if (!meta) return 'uncheckable';

  if (surnames.length > 0 && meta.authors && meta.authors.length > 0) {
    const metaTokens = authorTokens(meta.authors);
    const claimed = surnames.map(normToken).filter((s) => s.length >= 3);
    const anyMatch = claimed.some((s) => metaTokens.has(s));
    return anyMatch ? 'match' : 'mismatch';
  }
  if (years.length > 0 && meta.year != null) {
    return years.includes(meta.year) ? 'match' : 'mismatch';
  }
  return 'uncheckable';
}

// ---------------------------------------------------------------------------
// Catalog bridge — turn a cited catalog value into a GroundedValue
// ---------------------------------------------------------------------------

export interface MinimalCitation {
  readonly pmid?: string;
  readonly doi?: string;
  readonly note?: string;
}

export interface ResolvedMeta {
  readonly pmid?: string;
  readonly authors?: readonly string[];
  readonly year?: number;
  readonly title?: string;
}

/**
 * Wrap a catalog `CitedValue`/`CitedRange`-style value into a GroundedValue.
 *
 * - No citation at all → REFUSED (an uncited number is never presented).
 * - Citation present + resolved metadata supplied + the note's claim MISMATCHES the
 *   resolved paper → REFUSED (a wrongly-cited number is never presented — this is the
 *   anti-hallucination teeth at the data layer).
 * - Otherwise → a `cited` GroundedValue with `claimMatch` = match | uncheckable.
 */
export function groundCitedValue<T extends number>(
  citedValue: { value: T; citations?: readonly MinimalCitation[] },
  resolved?: ResolvedMeta | null,
  unit?: string,
): GroundedValue<T> {
  const c = citedValue.citations?.[0];
  if (!c || (!c.pmid && !c.doi)) return refuse<T>('value carries no citation');
  const verdict = claimMatch(c.note, resolved);
  if (verdict === 'mismatch') {
    return refuse<T>(
      `citation ${c.pmid ?? c.doi} does not match the claim${c.note ? ` "${c.note}"` : ''}`,
    );
  }
  return cited<T>(
    citedValue.value,
    {
      pmid: c.pmid ?? '',
      doi: c.doi,
      claimMatch: verdict === 'match' ? 'match' : 'uncheckable',
      note: c.note,
      title: resolved?.title,
      authors: resolved?.authors,
      year: resolved?.year,
    },
    unit,
  );
}

/** Render a one-line provenance string for a GroundedValue (for logs / chips). */
export function provenanceLabel(v: GroundedValue): string {
  const p = v.provenance;
  switch (p.kind) {
    case 'computed':
      return `computed · ${p.engine} · ${p.derivation}`;
    case 'cited':
      return `cited · PMID:${p.pmid || p.doi || '?'}${p.claimMatch === 'match' ? ' ✓claim' : ' (claim uncheckable)'}`;
    case 'refused':
      return `REFUSED · ${p.reason}`;
  }
}
